import axios from 'axios';
import store from './store';
import router from './router';

axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

const DEBUG = process.env.VUE_APP_DEBUG;
const ENDPOINT = process.env.VUE_APP_ENDPOINT;
const UPLOAD_ENDPOINT = process.env.VUE_APP_UPLOAD_ENDPOINT;

const CancelToken = axios.CancelToken;
let cancel;

const controllers = new Set();
let cancelled = false;

const axiosInstance = axios.create({
	baseURL: ENDPOINT,
	timeout: 1000 * 30
});

const uploadAxios = axios.create({
	baseURL: UPLOAD_ENDPOINT,
	timeout: 1000 * 30
});

const cancelAllRequests = () => {
	for (const controller of controllers) {
		if (controller.abort) {
			            controller.abort();
		}

	}
	controllers.clear();
}

axiosInstance.interceptors.request.use((config) => {
	const controller = new AbortController();
	config.signal = controller.signal;
	controllers.add(controller);
	config._controller = controller;
	// Modify the headers before the request is sent
	config.headers.Authorization = store.state.auth.authUserToken;
	return config;
});

axiosInstance.interceptors.response.use(response => {
	controllers.delete(response.config._controller);
	return response;
}, async (error, ) => {
	let result = () => Promise.reject(error);
	if (error.config?._controller) {
		controllers.delete(error.config._controller);
	}
	console.log(error, error?.response?.data?.errorMessage, JSON.stringify(error));
	if ([504, 0].includes(error?.response?.status)) {
		error = new Error("Request Timed Out");
	} else if (error?.response?.status === 403 || ["Token expired or doesn't exist", "UserToken Not Valid"].includes(error?.response?.data?.errorMessage)) {
		if (store?.state?.auth?.authUserToken) {
			await store.dispatch('clearAuthData');
			cancelAllRequests();
			if (router?.currentRoute?.name !== 'Login') {
				await router.push({
					name: "Login"
				});
			}
			result = () => Promise.reject(new Error("Your session has expired. Log back in to continue."));
		} else {
			result = () => Promise.resolve({});
		}
	}
	return result();
});

export const cancelPending = async (source) => {

	await source.cancel('Canceled by user');
	return true;

}

export const cancelRequests = (id) => {

	if (typeof cancel === 'function') {

		cancel('user cancel');
		return true;

	}

	return false;

};

export const isCancel = (err) => {
	return axios.isCancel(err);
};

const authorized = () => {
	let result = false;
	if (store?.state?.auth?.authUserToken) {
		axios.defaults.headers.common.Authorization = store.state.auth.authUserToken;
		result = true;
	}
	return result;
};

const getId = () => {
	return router?.currentRoute?.params?.id;
};

export const login = async (payload) => {

	console.log('api.js login payload', payload);

	let result = await axiosInstance.post('/users/login',  payload);

	console.log('api.js login result', result);

	return result;

};

export const session = async () => {

	authorized();

	let result

	try {
		result = await axiosInstance.get('/users/session');
	} catch (error) {
		console.log("error ==> ", error);
	}


	return result.data;

};

export const logout = async () => {

	authorized();

	let logoutResult

	try {
		logoutResult = await axiosInstance.get('/users/logout');
	} catch (error) {
		console.log("error ==> ", error);
	}

	return logoutResult;

};

export const changePassword = (payload) => {

	authorized();

	return axiosInstance.post('/users/password', payload);

};

export const summary = async (payload) => {

	authorized();

	let result = await axiosInstance.get('/applications/summary', payload);

	return result.data.results;

};

export const getOptions = async () => {

	authorized();

	let result

	try {
		result = await axiosInstance.get('/form-options');
		console.log('result', result);
	} catch (error) {
		console.log("error ==> ", error);
		return error;
	}


	return result.data ?? false;

};

export const getProgramAssetTypes = async () => {

	authorized();

	let result = await axiosInstance.get('/program/asset-types?programId=194');

	return result.data;

};

export const saveApplication = async (payload) => {

	const fieldsToUpperCase = [
		'address1',
		'address2',
		'city',
		'dealerEmail',
		'dealerName',
		'email',
		'employer',
		'employmentTitle',
		'firstName',
		'lastName',
		'middleInitial',
		'brand',
		'model',
	]

	fieldsToUpperCase.forEach(field => {
		if (payload[field]) {
			payload[field] = payload[field].toUpperCase();
		}
		if (payload.coApps) {
			payload.coApps = payload.coApps.map(coApp => {
				if (coApp[field]) {
					return { ...coApp, [field]: coApp[field].toUpperCase() };
				}
				return coApp;
			});
		}
		if (payload.equipments) {
			payload.equipments = payload.equipments.map(eqiupment => {
				if (eqiupment[field]) {
					return { ...eqiupment, [field]: eqiupment[field].toUpperCase() };
				}
				return eqiupment;
			});
		}
	});

	// Remove program from coApps if it exists (program should only be at root level)
	if (payload.coApps && Array.isArray(payload.coApps)) {
		payload.coApps = payload.coApps.map(coApp => {
			const { program, ...coAppWithoutProgram } = coApp;
			return coAppWithoutProgram;
		});
	}

	console.log('saveApplication payload', payload);

	if (!payload.token) {
		authorized();
	}

	console.log('pass authorized');

	if (payload.fakeApplcation) {
		payload.id = null;
	} else {
		payload.id = payload.id || getId();
	}

	let result = await axiosInstance.post('/applications/save', payload);

	return result?.data?.application;

};

export const saveCoBorrower = async (payload) => {

	authorized();

	payload.id = payload.id || getId();

	let result = await axiosInstance.post('/applications/save-co-applicant', payload);

	return result.data.application;

};

export const submitApplication = async (id) => {

	authorized();

	id = id || getId();

	let result = await axiosInstance.get(`/applications/submit/${id}`);

	return result.data.application;

};

export const callAction = async (payload) => {
	authorized();
	return await axiosInstance.post(`/action?action=${payload.action}&id=${payload.id}&for=${payload.for}&domain=${payload.domain}&dealer=${payload.dealer}&purpose=${payload.purpose}`, payload);
};

export const getApplication = async (id, token = null) => {
	if (!token) {
		authorized();
	}
	id = id || getId();
	let result = await axiosInstance.get(`/applications/get/${id}`, {
		params: {
			token: token || null
		}
	});
	return result.data;
};

export const getStatus = async (id) => {

	authorized();

	id = id || getId();

	let result = await axiosInstance.get(`/application/status/${id}`, {
		cancelToken: new CancelToken(function executor(c) {

			// An executor function receives a cancel function as a parameter
			cancel = c;

		})
	});

	return result.data;

};

export const getPermissions = async (id, source) => {

	authorized();

	id = id || getId();

	let result = await axiosInstance.get(`/application/permissions/${id}`, {
		cancelToken: source.token
	});

	return result.data;

};

export const runEvent = async (id, eventId) => {

	authorized();

	let result = await axiosInstance.get(`/application/event/${id}?eventId=${eventId}`);

	return result.data;

};

export const scan = (payload) => {

	authorized();

	let id = getId();

	let formData = new FormData();
	formData.append('fileInfo', payload);
	formData.append('fileType', payload.type);

	/*var reader = new FileReader();

	reader.onloadend = function() {

		console.log('RESULT', reader.result);

	};

	reader.readAsDataURL(payload);

	console.log(reader);*/

	return axiosInstance.post(`/applications/scan/${id}`, formData, {
		headers: {
			'Content-Type': false,
			'Authorization': store.state.auth.authUserToken
		}
	});

};


export const getSignNowStatusApi = async (payload) => {

	let result = await axiosInstance.get(`/get-sign-status/${payload.id}`, payload, {
		headers: {
			'Authorization': store.state.auth.authUserToken
		}
	});

	console.log('result', result);

	return result.data


}

export const sendCompletionCertificateSignNow = async (payload) => {

	authorized();

	const result = await axiosInstance.get(`/completion-certificate/${payload.id}`, {
		params: { method: payload.method },
	});

	return result.data;

};

export const getCompletionCertificateStatus = async (payload) => {

	authorized();

	const result = await axiosInstance.get(`/get-completion-certificate-status/${payload.id}`);

	return result.data;

};

export const upload = async (file, payload) => {
	console.log(`Upload request payload: ${JSON.stringify(payload)}`);
	const { contentType, id, token } = payload ?? {};
	if (!token) {
		authorized();
	}
	const authHeaders = { headers: { Authorization: store.state.auth.authUserToken }};
	const signedUrl = await axiosInstance.post(`/documents/upload/${id}`, payload, authHeaders);
	console.log(`Presigned url: ${signedUrl.data.url}, File being uploaded: ${file}`);
	delete axios.defaults.headers.common.Authorization;
	const s3Axios = axios.create({timeout: 1000 * 3000});
	const s3Result = await s3Axios.put(signedUrl.data.url, file, { headers: { 'Content-Type': contentType }});
	console.log(`Upload to s3 result: ${JSON.stringify(s3Result)}`);
	return s3Result;
};

export const notifyUpload = async (id, type) => {
	console.log(`Notifying about document upload: ${JSON.stringify({ id: id, type: type })}`);
	authorized();
	return await axiosInstance.get(`/documents/notify/${id}?type=${type}`);
}

export const uploadToEdge = async (payload) => {

	let result = await axiosInstance.get(`/applications/merge-and-upload/${payload.id}`, payload, {
		headers: {
			'Authorization': store.state.auth.authUserToken
		}
	});

	if (result.errorMessage) {
		return result;
	}

	return result.data;

}

export const share = (payload) => {

	authorized();

	payload.id = payload.id || getId();

	return axiosInstance.post('/share', payload);

};

export const search = async (status) => {

	authorized();

	let result = await axiosInstance.get(`/applications/search?status=${status}`);

	return result.data.results;

};

// TODO: replace hard-coded programId
export const getAssetTypes = async (id) => {

	authorized();

	let result = await axiosInstance.get(`/program/asset-types?programId=${id}`);

	console.log('getAssetTypes ', result);

	return result.data;

};

// payload: programId, assetTypeId
export const getAssetSubTypes = async (payload) => {

	authorized();

	let result = await axiosInstance.get(`/program/asset-sub-types?programId=194&assetTypeId=${payload.assetTypeId}`);

	console.log('getAssetSubTypes ', result);

	return result.data;

};

export const addAsset = async (payload) => {

	authorized();

	let assetId = payload.assetId ? 'assetId=' + payload.assetId : '';

	let installationDate = '';
	if (payload.installationDate) {
		installationDate = '&installationDate=' + payload.installationDate.replace(/\//g, '-');
		// console.log(installationDate);
		// return;
	}

	// TODO: construct URL params with loop
	let result = await axiosInstance.get(`/application/add-asset/${payload.id}?assetName=${payload.assetName}&assetTypeId=${payload.assetTypeId}&assetSubTypeId=${payload.assetSubTypeId}&serialNumber=${payload.serialNumber}&retailCost=${payload.retailCost}&${assetId}${installationDate}`);

	console.log('addAsset result', result);

	return result.data.result;

};

export const deleteAsset = async (id, assetId) => {

	authorized();

	let result = await axiosInstance.get(`/application/delete-asset/${id}?assetId=${assetId}`);

	console.log('deleteAsset result', result);

	return result.data.result;

};

export const getAssets = async (id) => {

	authorized();

	let result = await axiosInstance.get(`/application/get-assets/${id}`);

	console.log('getAssets result', result);

	return result.data.assets;

};

/** id is optional, used to check if assets are added */
export const getPrograms = async (id) => {

	authorized();

	let result = await axiosInstance.get('/programs', {
		params: {
			id: id
		}
	});

	// console.log('getPrograms result', result);

	return result.data;

};

export const getProgramOptions = async (payload) => {

	// console.log('getProgramOptions payload', payload);

	authorized();

	// let appId = payload.id ? "id=" + payload.id : '';
	// let amount = payload.amount ? "amount=" + payload.amount : '';

	let result = await axiosInstance.get('/program/options', {
		params: payload
	});

	// console.log('getProgramOptions result', result);

	return result.data;

};

export const getMonthlyPayment = async (payload) => {

	console.log('getMonthlyPayment payload', payload);

	authorized();

	let leaseDate = '';
	let installationDate = '';

	try {
		leaseDate = '&leaseDate=' + payload.leaseDate.replace(/\//g, '-');
		installationDate = '&installationDate=' + payload.installationDate.replace(/\//g, '-');
	} catch (error) {}

	let result;
	if (payload.sendToEdge) {
		result = await axiosInstance.get(`/application/save-program/${payload.id}?programId=${payload.programId}&termId=${payload.termId}${leaseDate}${installationDate}`);
	} else {
		result = await axiosInstance.get(`/program/payment/${payload.id}?programId=${payload.programId}&termId=${payload.termId}${leaseDate}${installationDate}`);
	}

	console.log('getMonthlyPayment result', result);

	return result.data;

};


export const saveProgram = async (payload) => {

	console.log('saveProgram payload', payload);

	authorized();

	let leaseDate = '';

	try {
		leaseDate = '&leaseDate=' + payload.leaseDate.replace(/\//g, '-');
	} catch (error) {}

	let result = await axiosInstance.get(`/application/save-program/${payload.id}?programId=${payload.programId}&termId=${payload.termId}${leaseDate}`);

	console.log('saveProgram result', result);

	return result.data;

};

export const uploadFile = async (payload) => {

	authorized();

	let result = await axiosInstance.post(`/applications/upload/${payload.id}`);

	console.log('uploadFile result', result);

	return result.data;

};


export const sendEmailInvite = async (payload) => {

	authorized();

	console.log('payload', payload);

	let result = await axiosInstance.get(`/sign/${payload.id}`, {
		params: {
			method: payload.method
		}
	});

	console.log('result:', result);

	return result.data;

};

export const resendSignNowInviteApi = async (payload) => {

	authorized();

	console.log('resendSignNowInviteApi payload', payload);

	let result = await axiosInstance.get(`/resend-sign-invite/${payload.id}`, {
		params: {
			method: payload.method
		}
	});

	console.log('resendSignNowInviteApi result:', result);

	return result.data;

};

export const downloadPdf = async (payload) => {

	authorized();

	console.log('payload', payload);

	let result = await uploadAxios.get(`/docusign/downloadPdf/${payload.id}`, {
		params: {
		}
	});

	console.log('result:', result);

	return result.data;

};

export const sendUploadTrigger = async (payload) => {

	authorized();

	console.log('id', payload.id);

	// uploadAxios

	let result = await uploadAxios.get(`/docusign/upload-to-s3/${payload.id}?envelopeId=${payload.envelopeId}`);

	console.log('sendUploadTrigger result:', result);

	return result;

}

export const checkSignStatusOld = async (payload) => {

	if (!payload.envelopeId) {
		return;
	}

	authorized();

	console.log('id', payload.id);

	// uploadAxios

	let result;

	try {

		result = await uploadAxios.get(`/check-sign/${payload.id}?envelopeId=${payload.envelopeId}`);

	} catch (error) {

		console.log('error', error);

		return false;

	}

	console.log('checkSignStatus result:', result);

	return result;

}

export const checkUploadsStatus = async (id) => {

	authorized();

	console.log('id', id);

	let result;

	try {

		result = await uploadAxios.get(`/applications/check-upload-status/${id}`);

	} catch (error) {

		console.log('error', error);

	}

	console.log('checkUploadsStatus result:', result);

	return result;

}

export const sync = async () => {

	authorized();

	let result = await axiosInstance.get('/applications/sync');

	return result.data.results;

};

/** POST body: { items: [{ id, status }] } — canonical application.status per id. */
export const pollEdgeStatusesForDashboard = async (items) => {

	authorized();

	const result = await axiosInstance.post('/applications/poll-edge-statuses', { items });

	return result.data.updates || [];

};

export const getSignedUrl = async (payload) => {

	authorized();

	let result = await axiosInstance.get(`/get-signed-url/${payload.id}`, {
		params: {
			key: payload.key
		}
	});

	return result.data;

};

export const getDocusealUrl = async (id) => {
	authorized();
	const result = await axiosInstance.get(`/docuseal/get-signed-document/${id}`);
	return result.data;
}

export const bankVerification = async (id) => {

	authorized();

	let result = await axiosInstance.get(`/applications/bank-verification/${id}`);

	return result.data;

};

export const dailyApplicationsLogExport = async () => {

	authorized();

	let result = await axiosInstance.get('/daily-export', {
		headers: {
			'x-user-email': store.state.auth.authUser?.email || null
		}
	});

	return result.data;

};

export const monthlyApplicationsLogExport = async () => {

	authorized();

	let result = await axiosInstance.get('/monthly-export', {
		headers: {
			'x-user-email': store.state.auth.authUser?.email || null
		}
	});

	return result.data;

};

export async function getTaxRates() {
    const response = await axiosInstance.get('/api/taxes'); 
    return response.data;
};
